import { useSetAtom } from 'jotai';
import { useIntl } from 'react-intl';
import useAPI from '../../../api';
import useNotify from '../../../Hooks/useNotify';
import { openSnackbarAdviseAtom } from '../../../Atoms';
import { useInvalidateSectionLayouts } from '../../../Hooks/useSectionLayout';
import { atDialect } from '../dialect';

/**
 * Saves and deletes a section's layouts, reporting each in a snackbar and dropping the section's
 * cached layouts once it is done.
 *
 * @returns {Object} `{ save, remove }`.
 */
const useLayoutWrites = () => {
  const API = useAPI();
  const intl = useIntl();
  const openSnackbar = useSetAtom(openSnackbarAdviseAtom);
  const { notify } = useNotify();
  const invalidateLayouts = useInvalidateSectionLayouts();

  /**
   * Saves a layout as the section's default, stamped with its dialect.
   *
   * @param {Object} row - `{ id, sectionName, subSectionId, surface, name, layout }`; `id` is null for
   *   a new one, and a blank name saves under the section's.
   * @param {Function} onSaved - Called after the save.
   */
  const save = (
    { id, sectionName, subSectionId = null, surface, name, layout },
    onSaved
  ) =>
    API.saveSectionLayout(
      {
        id,
        sectionName,
        subSectionId,
        surface,
        name: name || sectionName,
        jsonStructure: JSON.stringify(atDialect(layout)),
        isDefault: true,
      },
      () => {
        notify('CONFIGURATION_SAVED', 'success');
        invalidateLayouts(sectionName);
        onSaved();
      },
      (err) =>
        openSnackbar({
          message: `${intl.messages.ERROR}: ${err?.status ?? ''}`,
          severity: 'error',
        })
    );

  /**
   * Deletes a saved layout.
   *
   * @param {Number} id - The layout's id.
   * @param {String} sectionName - The section it belongs to.
   * @param {Function} onRemoved - Called after the delete.
   */
  const remove = (id, sectionName, onRemoved) =>
    API.deleteSectionLayout(
      id,
      () => {
        notify('LAYOUT_DELETED', 'success');
        invalidateLayouts(sectionName);
        onRemoved();
      },
      () => {}
    );

  return { save, remove };
};

export default useLayoutWrites;
